import React from 'react';
import { tv } from 'tailwind-variants';
import { ReactComponent as ArrowRight } from '@/assets/v2/icons/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '@/assets/v2/icons/arrow-left.svg';

const button = tv({
  base: 'rounded-full font-normal w-fit flex items-center overflow-x-hidden relative transition-all duration-500',
  variants: {
    variant: {
      primary:
        'px-1.5 py-1 bg-[#F7F7F7] shadow-[inset_0px_2px_14px_0px_rgba(0,0,0,0.06)] text-black group/primary-button',
      secondary:
        'px-5 py-[1rem] border border-[#E5E5E5] bg-white text-orange hover:border-orange',
      dark: 'px-5 py-[1rem] border border-[#E5E5E5] bg-[rgba(255,255,255,0.10)] backdrop-blur-[12px] hover:border-orange text-orange',
      faded:
        'px-5 py-[1rem] border border-transparent bg-[rgba(255,255,255,0.30)] text-white backdrop-blur-[3px] hover:backdrop-blur-0',
    },
    fullWidth: {
      true: 'w-full',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

const leftArrow = tv({
  base: 'transition-all duration-500 absolute left-0.5',
  variants: {
    variant: {
      primary:
        'w-0 scale-0 group-hover/primary-button:scale-100 group-hover/primary-button:w-6',
      secondary: '',
      dark: '',
      faded: '',
    },
  },
});

const rightArrow = tv({
  base: 'transition-all duration-500 absolute right-1',
  variants: {
    variant: {
      primary:
        'w-8 scale-100 group-hover/primary-button:scale-0 group-hover/primary-button:w-0',
      secondary: '',
      dark: '',
      faded: '',
    },
  },
});

const textWrapper = tv({
  base: 'relative z-10',
  variants: {
    variant: {
      primary: 'px-5 py-3',
      secondary: '',
      dark: '',
      faded: '',
    },
  },
});

const text = tv({
  base: 'relative z-10 transition-all duration-500',
  variants: {
    variant: {
      primary: 'px-4 py-3 -left-5 group-hover/primary-button:left-2.5',
      secondary: '',
      dark: '',
      faded: '',
    },
  },
});

const textShadow = tv({
  base: 'absolute transition-all duration-500',
  variants: {
    variant: {
      primary:
        'left-0 right-9 top-0 bottom-0 bg-gradient-to-b from-white to-[#f3f3f3] rounded-full shadow-[0px_93px_26px_0px_rgba(0,0,0,0.00),_0px_59px_24px_0px_rgba(0,0,0,0.01),_0px_33px_20px_0px_rgba(0,0,0,0.05),_0px_15px_15px_0px_rgba(0,0,0,0.09),_0px_4px_8px_0px_rgba(0,0,0,0.10)] group-hover/primary-button:left-6 group-hover/primary-button:right-0',
      secondary: '',
      dark: '',
      faded: '',
    },
  },
});

interface ButtonProps<C extends React.ElementType = 'button'> {
  variant?: 'primary' | 'secondary' | 'dark' | 'faded';
  fullWidth?: boolean;
  children: React.ReactNode;
  as?: C;
  className?: string;
  arrowDirection?: 'left' | 'right';
}

type ButtonComponentProps<C extends React.ElementType> = ButtonProps<C> &
  Omit<React.ComponentPropsWithoutRef<C>, keyof ButtonProps<C>>;

export const Button = <C extends React.ElementType = 'button'>({
  variant,
  fullWidth,
  children,
  as,
  className,
  arrowDirection = 'right',
  ...props
}: ButtonComponentProps<C>) => {
  const Component = as || 'button';
  const ArrowIcon = arrowDirection === 'left' ? ArrowLeft : ArrowRight;
  
  return (
    <Component className={button({ variant, fullWidth, className })} {...props}>
      {variant === 'primary' && (
        <ArrowIcon className={leftArrow({ variant })} />
      )}
      <div className={textWrapper({ variant })}>
        <span className={text({ variant })}>{children}</span>
        <span className={textShadow({ variant })} />
      </div>
      {variant === 'primary' && (
        <ArrowIcon className={rightArrow({ variant })} />
      )}
    </Component>
  );
};

export default Button;
